import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { VehicleDetails } from '../../../API/types/Vehicles';
import { DriverDetails } from '../../../API/types/Drivers';
import useVehicleThumbnail from '../../../theme/useVehicleThumbnail';
import useDriverThumbnail from '../../../theme/useDriverThumbnail';
import Tags from '../Tags';
import ItemsListSkeleton from '../Skeleton/ItemsListSkeleton';
import { ThemeType } from '../../../theme/themeType';
import S from './EntityRatingStyles';
import { generateScoreColor, getScoreLabel } from '../../../utils/commonUtils';
import { generateDistance } from '../../../utils/unitsUtils';

type Props = {
  title: string;
  isLoading: boolean;
  entityType: 'vehicle' | 'driver';
  entities?: VehicleDetails[] | DriverDetails[];
  ratingField?: 'score' | 'scoreDaily' | 'scoreWeekly' | 'scoreMonthly' | 'distanceInKmDaily' | 'distanceInKmWeekly' | 'distanceInKmMonthly' | 'distanceInKmTotal' | 'alertCountDaily' | 'alertCountWeekly' | 'alertCountMonthly';
  emptyMessage?: string;
};

function EntityRating({
  title, entityType, entities, isLoading, ratingField = 'score', emptyMessage,
}: Props): JSX.Element {
  const navigate = useNavigate();
  const theme = useTheme() as ThemeType;
  const vehicleThumbnail = useVehicleThumbnail();
  const driverThumbnail = useDriverThumbnail();
  return (
    <S.Wrapper>
      <div className="title">{title}</div>
      <div className="entity-container">
        {isLoading && <ItemsListSkeleton length={2} />}
        {!isLoading && emptyMessage && (!entities || entities.length === 0) && (
          <div style={{ opacity: 0.5, textAlign: 'center', padding: '12px 0' }}>{emptyMessage}</div>
        )}
        {!isLoading && entities?.map((entity) => (
          <div
            role="presentation"
            key={entity._id}
            className="entity"
            onClick={() => navigate(`/${entityType === 'vehicle' ? 'vehicles' : 'drivers'}/${entity._id}`)}
          >
            <Tags.ItemOverview
              avatar={entity.avatarUrl || (entityType === 'vehicle' ? vehicleThumbnail : driverThumbnail)}
              avatarAlt={entityType === 'vehicle' ? 'vehicle' : 'driver'}
              title={entityType === 'vehicle' ? (entity as VehicleDetails).licensePlateNumber : `${(entity as DriverDetails).firstName} ${(entity as DriverDetails).lastName}`}
              subtitle=""
            />
            {['score', 'scoreDaily', 'scoreWeekly', 'scoreMonthly'].includes(ratingField) && entity[ratingField] != null && (
              <Tags.Status
                label={getScoreLabel(entity[ratingField] as number)}
                backgroundColor={generateScoreColor(entity[ratingField] as number)}
              />
            )}
            {['score', 'scoreDaily', 'scoreWeekly', 'scoreMonthly'].includes(ratingField) && entity[ratingField] == null && (
              <Tags.Status label="No score" backgroundColor={theme.colors.outline} title="No data for this period yet." />
            )}
            {['distanceInKmTotal', 'distanceInKmDaily', 'distanceInKmWeekly', 'distanceInKmMonthly'].includes(ratingField) && (
              <Tags.Label
                label={entity[ratingField] != null ? generateDistance(entity[ratingField]!, ratingField === 'distanceInKmTotal') : 'N/A'}
              />
            )}
            {['alertCountDaily', 'alertCountWeekly', 'alertCountMonthly'].includes(ratingField) && (
              <Tags.Label
                label={entity[ratingField] !== undefined ? `${entity[ratingField]} alerts` : 'N/A'}
              />
            )}
          </div>
        ))}
      </div>
    </S.Wrapper>
  );
}

export default EntityRating;
